import {
  forwardRef,
  useId,
  useState,
  type ChangeEvent,
  type InputHTMLAttributes,
} from "react";

import SliderThumbIcon from "@/assets/icons/slider-thumb.svg?react";
import { cn } from "@/lib/utils";

export type ClientSliderProps = {
  value?: number;
  defaultValue?: number;
  onValueChange?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  showValue?: boolean;
  formatValue?: (value: number) => string;
} & Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "value" | "defaultValue" | "onChange" | "min" | "max" | "step"
>;

/**
 * TCI client percentage slider (Figma 412:3891).
 */
export const ClientSlider = forwardRef<HTMLInputElement, ClientSliderProps>(
  function ClientSlider(
    {
      value: valueProp,
      defaultValue = 0,
      onValueChange,
      min = 0,
      max = 100,
      step = 1,
      showValue = true,
      formatValue = (value) => `${value}%`,
      className,
      disabled,
      id,
      ...props
    },
    ref,
  ) {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const isControlled = valueProp !== undefined;
    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
    const value = isControlled ? valueProp : uncontrolledValue;
    const clamped = Math.min(max, Math.max(min, value));
    const percent = max === min ? 0 : ((clamped - min) / (max - min)) * 100;

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
      const next = Number(event.target.value);
      if (!isControlled) {
        setUncontrolledValue(next);
      }
      onValueChange?.(next);
    }

    return (
      <div className={cn("flex w-full flex-col", className)}>
        {showValue ? (
          <div
            aria-hidden
            className="w-full text-right font-['Inter'] text-[28px] font-medium leading-9 tracking-[-0.7px] text-[#38bdf8] sm:text-[36px] sm:leading-[42px] sm:tracking-[-0.9px]"
          >
            {formatValue(clamped)}
          </div>
        ) : null}

        <div className="relative h-6 w-full">
          <div
            aria-hidden
            className="absolute left-0 right-0 top-2 h-2 rounded bg-[#eaecf0]"
          />
          <div
            aria-hidden
            className="absolute left-0 top-2 h-2 rounded bg-[#38bdf8]"
            style={{ width: `${percent}%` }}
          />
          <SliderThumbIcon
            aria-hidden
            width={36}
            height={36}
            className="pointer-events-none absolute size-9 max-w-none"
            style={{
              left: `calc(${percent}% - 18px)`,
              top: "-2px",
            }}
          />
          <input
            ref={ref}
            id={inputId}
            type="range"
            min={min}
            max={max}
            step={step}
            value={clamped}
            disabled={disabled}
            onChange={handleChange}
            className={cn(
              "absolute inset-0 z-10 m-0 h-6 w-full cursor-pointer appearance-none bg-transparent",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "[&::-webkit-slider-runnable-track]:h-2 [&::-webkit-slider-runnable-track]:rounded [&::-webkit-slider-runnable-track]:bg-transparent",
              "[&::-webkit-slider-thumb]:size-6 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-transparent",
              "[&::-moz-range-track]:h-2 [&::-moz-range-track]:rounded [&::-moz-range-track]:bg-transparent",
              "[&::-moz-range-thumb]:size-6 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-transparent",
            )}
            {...props}
          />
        </div>
      </div>
    );
  },
);
